import { ChangeEventHandler, forwardRef, RefAttributes, SelectHTMLAttributes } from "react";
import {
  borderClassName,
  inputClassName,
  inputColorClassName,
  getFocusOutline,
  roundedClassName,
  textSizeClassName,
} from "./commonClassNames";
import { BorderProps, InputColorProps } from "./commonProps";
import { Icon } from "./Icon";
import { Span } from "./Span";

// This is almost like OptionHTMLAttributes in @types/react/index.d.ts
export type InputSelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
  selected?: boolean | undefined;
};

export type InputSelectOnChange = (options: InputSelectOption["value"]) => void;

type InputSelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, "className" | "onChange"> &
  RefAttributes<HTMLSelectElement> &
  BorderProps &
  InputColorProps & {
    onChange?: InputSelectOnChange;
    selected?: InputSelectOption["value"];
    options?: InputSelectOption[];
    placeholder?: string;
    small?: boolean;
    className?: string;
  };

export const InputSelect: React.FC<InputSelectProps> = forwardRef(
  (
    {
      onChange: onChangeOption,
      selected,
      options = [],
      placeholder = "",
      border,
      grouped,
      neutral,
      transparent,
      small,
      className,
      ...props
    },
    ref,
  ) => {
    const selectClassName = [
      "appearance-none w-full font-normal",
      small === true ? "p-1 h-8" : "pr-10 h-12",
      inputClassName(),
      roundedClassName({ grouped }),
      borderClassName({ border, grouped }),
      inputColorClassName({ neutral, transparent }),
      textSizeClassName({ size: small ? "sm" : "base" }),
      getFocusOutline({ border }),
    ].join(" ");

    const onChange: ChangeEventHandler<HTMLSelectElement> = (event) => {
      onChangeOption?.(event.target.value);
    };

    const optionsList = [{ disabled: true, value: "", label: placeholder }, ...options];

    const selectedValue = options.find((option) => option.value === selected)?.value ?? "";

    return (
      <div className={`relative ${className}`}>
        <div className="absolute top-0 right-0 h-full w-12 grid place-content-center pointer-events-none">
          <Span neutral>
            <Icon name="caret-down" />
          </Span>
        </div>
        <select className={selectClassName} onChange={onChange} value={selectedValue} {...props} ref={ref}>
          {optionsList.map(({ disabled, value, label }) => (
            <option key={value} value={value} disabled={disabled}>
              {label}
            </option>
          ))}
        </select>
      </div>
    );
  },
);
